<template>
  <div class="sm:c-col-4 md:c-col-8 lg:c-col-6 p-access-key-container">
    <p-form
      @submit="onFormSubmit"
      v-slot="{ invalid }"
    >
      <!-- Título -->
      <div class="c-row">
        <div class="c-col">
          <h3 class="c-text-h3 ui-mb-6">
            Informe o seu código
          </h3>
        </div>
      </div>

      <!-- Subtítulo -->
      <div class="c-row ui-mb-4">
        <div class="c-col">
          <div class="c-text-b1 c-text-gray-400">
            Confirme o código enviado para o email<br>
            <strong>{{ email }}</strong>
          </div>
        </div>
      </div>

      <!-- Código -->
      <div class="c-row ui-mb-4">
        <validation-provider
          class="c-col"
          rules="required"
          name="Código"
          v-slot="{ errors }"
        >
          <p-input
            v-model="code"
            data-cy="codeField"
            label="Código"
            type="text"
            :error-message="errors[0]"
          />
        </validation-provider>
      </div>

      <!-- Botões -->
      <div class="c-row ui--mb-4">
        <!-- Voltar -->
        <div class="c-col-2 sm:c-col ui-mb-4">
          <template v-if="$slots.back">
            <slot name="back" />
          </template>
          <router-link
            v-else
            :to="{ name: 'enviar-chave-de-acesso', query: { ...$route.query } }"
          >
            <c-button
              :outline="true"
              class="ui-w-full"
              size="lg"
            >
              Voltar
            </c-button>
          </router-link>
        </div>

        <!-- Validar código -->
        <div class="c-col-2 sm:c-col ui-mb-4">
          <c-button
            data-cy="validateButton"
            size="lg"
            type="submit"
            class="ui-w-full"
            :disabled="invalid"
            :loading="isFormSending"
          >
            Validar código
          </c-button>
        </div>
      </div>
    </p-form>

    <!-- Reenviar código -->
    <div class="c-row ui-mt-4">
      <div class="c-col ui-text-center ui-mb-4 ui-mt-8">
        <span
          v-if="currentResendCounter > 0"
          class="c-text-b2 resend-disabled"
        >
          Reenviar código em {{ resendCounterLabel }}
        </span>
        <c-link
          v-else
          class="c-text-b2"
          @click="onFormResendCode"
        >
          Reenviar código
        </c-link>
      </div>
    </div>

    <!-- Divisor "Ou" -->
    <div class="login-divider ui-my-6">
      <div class="login-divider__line" />
      <span class="login-divider__text c-text-b2">Ou</span>
      <div class="login-divider__line" />
    </div>

    <!-- Já é aluno? -->
    <div class="c-row ui-mt-8">
      <div class="c-col">
        <div class="ui-flex ui-flex-col c-text-gray">
          <span class="c-text-b1 c-text-gray-500 ui-mb-2"><strong>Já é aluno?</strong></span>
          <template v-if="$slots.login">
            <slot name="login" />
          </template>
          <router-link
            v-else
            :to="{ name: 'login', query: { ...$route.query } }"
            data-cy="loginLink"
            class="width-fit"
          >
            <c-link class="padding-remover">
              Faça login com outro email
            </c-link>
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { CButton, CLink } from '@estrategiahq/coruja-web-ui';
import PForm from '@/components/p-form';
import PInput from '@/components/p-input';
import { ValidationProvider } from 'vee-validate';

import { extractUtmFromCookies } from '@/utils/utm';
import { ACCOUNTS_OAUTH_CALLBACK_URL } from '@/consts';
import { invalidPasswordlessCodeError } from '@/utils/errors';
import { redirectToTarget } from '@/utils/url';
import { getToken } from '@/services/token';

const RESEND_COUNTER_TIME = 60;

export default {
  name: 'PConfirmeSeuCodigo',
  props: {
    preventNavigation: {
      type: Boolean,
      default: false,
    },
    emailProp: {
      type: String,
      default: '',
    },
  },
  components: {
    PForm,
    PInput,
    CButton,
    CLink,
    ValidationProvider,
  },
  data: () => ({
    email: '',
    code: '',
    isFormSending: false,
    currentResendCounter: RESEND_COUNTER_TIME,
    resendCounterStartedAt: new Date(),
    resendCounterTimeout: null,
  }),
  computed: {
    resendCounterLabel() {
      const minutes = Math.floor(this.currentResendCounter / 60).toString().padStart(2, '0');
      const seconds = (this.currentResendCounter % 60).toString().padStart(2, '0');
      return `${minutes}:${seconds}`;
    },
  },
  beforeDestroy() {
    if (this.resendCounterTimeout) {
      clearInterval(this.resendCounterTimeout);
    }
  },
  mounted() {
    if (this.emailProp) {
      this.email = this.emailProp.trim();
    } else if (!this.$route.query.email) {
      this.$router.push({ name: 'login' });
      return;
    } else {
      this.email = this.$route.query.email.trim() || '';
    }
    this.startResendCounter();
  },
  methods: {
    updateResendCounter() {
      const newValue = RESEND_COUNTER_TIME - Math.floor((new Date() - this.resendCounterStartedAt) / 1000);
      if (newValue <= 0) {
        this.currentResendCounter = 0;
        clearInterval(this.resendCounterTimeout);
        this.resendCounterTimeout = null;
        return;
      }
      this.currentResendCounter = newValue;
    },
    startResendCounter() {
      this.currentResendCounter = RESEND_COUNTER_TIME;
      this.resendCounterStartedAt = new Date();
      this.resendCounterTimeout = setInterval(() => {
        this.updateResendCounter();
      }, 1000);
    },
    async onFormSubmit() {
      this.isFormSending = true;
      try {
        const data = await this.$services.accounts.validatePasswordlessKey(this.email, this.code);
        this.loginSuccessHandler(data);
      } catch (e) {
        localStorage.setItem('last_login_failed', true);
        this.$toast.show({
          type: 'error',
          text: e === invalidPasswordlessCodeError
            ? 'Código inválido. Tente novamente.'
            : 'Não foi possível validar o código. Tente novamente.',
          timeout: 3000,
        });
      } finally {
        this.isFormSending = false;
      }
    },
    loginSuccessHandler(logindata) {
      if (this.preventNavigation) {
        this.$emit('logged-in', logindata);
        return;
      }

      if (this.$route.query.parceiro) {
        localStorage.setItem('partner@estrategia', this.$route.query.parceiro);
      }

      if (logindata.oauth_callback_url) {
        localStorage.setItem(ACCOUNTS_OAUTH_CALLBACK_URL, logindata.oauth_callback_url);
        if (localStorage.getItem(ACCOUNTS_OAUTH_CALLBACK_URL)) {
          this.$router.push({ name: 'oauth-callback-redirect' });
        }
        localStorage.removeItem('last_login_failed');
        return;
      }

      this.$services.events.notifyManualLogin();
      this.persistUtmIfExists();
      localStorage.removeItem('last_login_failed');

      if (this.$route.query.returnTo) {
        try {
          const returnToQuery = this.$route.query.returnToQuery ?
            JSON.parse(this.$route.query.returnToQuery) : {};

          this.$router.push({
            name: this.$route.query.returnTo,
            query: returnToQuery,
          });
          return;
        } catch (e) {
          console.error('Erro ao fazer redirecionamento de volta:', e);
        }
      }

      redirectToTarget({
        target: this.$route.query.target,
        token: getToken(),
        allowedHosts: this.$env.VUE_APP_ALLOWED_TARGET_HOSTS.split(','),
        fallback: () => this.$router.push('/'),
      });
    },
    async persistUtmIfExists() {
      try {
        const utm = extractUtmFromCookies();
        if (utm) {
          this.$services.bff.setUtm(utm);
        }
      } catch (_) {
        // possível erro não deve ser mostrado ao usuário
      }
    },
    async onFormResendCode() {
      try {
        await this.$services.accounts.sendLoginPasswordlessKeyEmail(this.email);
        this.$toast.show({
          type: 'success',
          text: 'Email de verificação enviado com sucesso.',
          timeout: 5000,
        });
        this.startResendCounter();
        if (!this.preventNavigation) {
          this.$router.push({ name: 'confirme-seu-codigo', query: { ...this.$route.query, email: this.email } });
        }
      } catch (e) {
        this.$toast.show({
          type: 'error',
          text: 'Não foi possível enviar o email. Tente novamente.',
          timeout: 3000,
        });
      }
    },
  },
};
</script>

<style scoped>
.p-access-key-container {
  padding: 2rem !important;
}

.login-divider {
  display: flex;
  align-items: center;
  gap: 16px;
}

.login-divider__line {
  flex: 1;
  height: 1px;
  background: #ccc;
  opacity: 0.48;
}

.login-divider__text {
  color: #9e9e9e;
  white-space: nowrap;
}

.padding-remover {
  padding: 0 !important;
  border: none !important;
}

.resend-disabled {
  color: var(--c-color-gray-300);
  text-decoration: underline;
  text-decoration-color: var(--c-color-gray-300);
  font-weight: 700;
}

.width-fit {
  width: fit-content !important;
}
</style>
